import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Paper } from '@mui/material';
import { getReceivedInvitations, acceptInvitation, declineInvitation } from '../services/authService';
import { getProfile } from '../services/authSlice';

function ReceivedInvitationsList({ refreshKey }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const statusLabels = {
    pending: { label: t('pages.profile.receivedInvitations.statusPending'), className: 'bg-warning text-dark' },
    accepted: { label: t('pages.profile.receivedInvitations.statusAccepted'), className: 'bg-success' },
    declined: { label: t('pages.profile.receivedInvitations.statusDeclined'), className: 'bg-danger' },
    expired: { label: t('pages.profile.receivedInvitations.statusExpired'), className: 'bg-secondary' },
  };

  const fetchInvitations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getReceivedInvitations();
      setInvitations(response.data || []);
    } catch (err) {
      setError(t('pages.profile.receivedInvitations.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [refreshKey]);

  const handleAccept = async (token) => {
    setActionLoading(token);
    try {
      await acceptInvitation(token);
      await fetchInvitations();
      dispatch(getProfile());
    } catch (err) {
      setError(err.response?.data?.error || t('pages.profile.receivedInvitations.acceptError'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (token) => {
    setActionLoading(token);
    try {
      await declineInvitation(token);
      await fetchInvitations();
    } catch (err) {
      setError(err.response?.data?.error || t('pages.profile.receivedInvitations.declineError'));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">{t('pages.profile.receivedInvitations.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger mb-0">{error}</div>
    );
  }

  const getDisplayStatus = (inv) => {
    if (inv.status === 'pending' && inv.isExpired) {
      return statusLabels.expired;
    }
    return statusLabels[inv.status] || { label: inv.status, className: 'bg-secondary' };
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <div>
        <h4 className="mb-3">{t('pages.profile.receivedInvitations.title')}</h4>
        {invitations.length === 0 ? (
          <p className="text-muted">{t('pages.profile.receivedInvitations.noInvitations')}</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>{t('pages.profile.receivedInvitations.columnSender')}</th>
                  <th>{t('pages.profile.receivedInvitations.columnChildren')}</th>
                  <th>{t('pages.profile.receivedInvitations.columnStatus')}</th>
                  <th>{t('pages.profile.receivedInvitations.columnSentAt')}</th>
                  <th>{t('pages.profile.receivedInvitations.columnExpiresAt')}</th>
                  <th>{t('pages.profile.receivedInvitations.columnActions')}</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv) => {
                  const displayStatus = getDisplayStatus(inv);
                  const isPending = inv.status === 'pending' && !inv.isExpired;
                  return (
                    <tr key={inv.id}>
                      <td>
                        <small>{inv.invitingUserName || inv.invitingUserEmail || '-'}</small>
                      </td>
                      <td>
                        <small>
                          {inv.children?.map((c) => c.name).join(', ') || '-'}
                        </small>
                      </td>
                      <td>
                        <span className={`badge ${displayStatus.className}`}>
                          {displayStatus.label}
                        </span>
                      </td>
                      <td>
                        <small>
                          {inv.createdAt
                            ? new Date(inv.createdAt).toLocaleDateString('hu-HU')
                            : '-'}
                        </small>
                      </td>
                      <td>
                        <small>{inv.expiresAt || '-'}</small>
                      </td>
                      <td>
                        {isPending && (
                          <div className="d-flex gap-1">

                            <Button
                              size="small"
                              variant="contained"
                              component="label"
                              onClick={() => handleAccept(inv.token)}
                              disabled={actionLoading !== null}
                              color="success"
                            >
                              {actionLoading === inv.token ? '...' : t('pages.profile.receivedInvitations.accept')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              component="label"
                              onClick={() => handleDecline(inv.token)}
                              disabled={actionLoading !== null}
                              color="error"
                            >
                              {actionLoading === inv.token ? '...' : t('pages.profile.receivedInvitations.decline')}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Paper>
  );
}

ReceivedInvitationsList.propTypes = {
  refreshKey: PropTypes.number,
};

ReceivedInvitationsList.defaultProps = {
  refreshKey: 0,
};

export default ReceivedInvitationsList;
